<template>
    <div>
        <div>
            <DefaultCard title="eSIM Profiles"
                         teaser="A user can have multiple eSIM profiles at the same time."
                         :is-busy="isLoadingProfile"
                         :error="profilesError"
                         class="dataplan-infos mb-6">
                <template #default>
                    <TableList v-if="profiles.length > 0"
                               :columns="profilesTableColumns"
                               :rows="profiles"
                               :is-clickable="true"
                               @row-clicked="showProfile($event)"
                               empty-text="No eSIM profiles available.">
                        <template slot="column_status" slot-scope="{data}">
                            <span :class="{'green': data.esimState.toLowerCase() === 'installed'}" v-if="data.esimState">{{ data.esimState }}</span>
                        </template>
                        <template slot="column_lastActive" slot-scope="{data}">
                            <span class="date" v-if="data.lastSeen">{{ data.lastSeen | dateFormat }}</span>
                        </template>
                        <template slot="column_lastCountry" slot-scope="{data}">
                            <span class="date" v-if="data.lastCountryCode">{{ data.lastCountryCode }}</span>
                        </template>
                        <template slot="column_iccid" slot-scope="{data}">
                            {{ data.iccid }}
                        </template>
                    </TableList>
                    <TDSButton v-else  class="mt-6" button-style="primary-red"
                               @click="showModal = 'addProfile'">{{ $t("dataPlans.addProfile") }}</TDSButton>
                </template>
            </DefaultCard>

            <DefaultCard title="Plans"
                         teaser="The eSIM profiles attached to the user are using the same data plans!"
                         :is-busy="isLoadingPlans"
                         :error="plansError || deductError"
                         class="mb-6">
                <template #default>

                    <TableList :columns="dataPlansTableColumns"
                               :rows="dataPlans"
                               empty-text="No plans available." v-if="!isMobile">
                        <template slot="column_bonus" slot-scope="{data}">
                            <div>
                                <span v-if="data.fixed" class="isBonus" v-tooltip="'Has data plans'" />
                            </div>
                        </template>
                        <template slot="column_status" slot-scope="{data}">
                            {{ data.active ? "Active" : "Inactive" }}
                        </template>
                        <template slot="column_balance" slot-scope="{data}">
                            {{ data.balanceInByte | byteFormat }}
                        </template>
                        <template slot="column_country" slot-scope="{data}">
                            {{ data.countrySetSymbol }}
                        </template>
                        <template slot="column_expiry" slot-scope="{data}">
                            {{ data.expiresAt | dateFormat }}
                        </template>
                        <template slot="column_deduct" slot-scope="{data}">
                            <a v-if="!data.fixed" @click.stop="openDeductModal(data)" class="deductLink">Deduct</a>
                        </template>
                    </TableList>
                    <div v-else-if="isMobile" v-for="(entry, index) in dataPlans" :key="index" class="card-item">
                        <div>
                            <div v-if="entry.fixed" ><span class="isBonus"/></div>
                            <div v-else><span style="width: 30px; height: 30px; display: block"></span></div>
                        </div>
                        <div class="content">
                            <div>
                                <div><span>{{entry.balanceInByte | byteFormat}}</span></div>
                                <div><span>{{entry.expiresAt | dateFormat}}</span></div>
                            </div>
                            <div>
                                <div><span>{{entry.countrySetSymbol}}</span></div>
                                <div>
                                    <span v-if="entry.active" style="color: green">active</span>
                                    <span v-if="!entry.active" style="color: red">inactive</span>
                                </div>
                            </div>
                            <div>
                                <div><a v-if="!entry.fixed" @click.stop="openDeductModal(data)" class="deductLink">Deduct</a></div>
                            </div>
                        </div>
                    </div>

                </template>
            </DefaultCard>


            <DefaultCard title="Topup eSIM Balance"
                         teaser="Easily topup the eSIM balance of a user with a world wide balance of a given size."
                         :error="topUpError"
                         class="mb-6">
                <template #default>
                    <div>
                        <div class="amount-input">
                            <InputField :value.sync="topupAmount" label="Amount" />
                            <div class="amount-units">
                                <TDSCheckbox :activated="topupUnit === 'KB'" @update:activated="topupUnit = 'KB'">KB</TDSCheckbox>
                                <TDSCheckbox :activated="topupUnit === 'MB'" @update:activated="topupUnit = 'MB'">MB</TDSCheckbox>
                                <TDSCheckbox :activated="topupUnit === 'GB'" @update:activated="topupUnit = 'GB'">GB</TDSCheckbox>
                            </div>
                        </div>

                        Please select a reason for the topup:
                        <TDSCheckbox :activated.sync="isQaTopup">QA</TDSCheckbox>
                        <TDSCheckbox :activated="!isQaTopup" @update:activated="isQaTopup = !$event">Marketing</TDSCheckbox>
                        <TDSButton button-style="primary-red" @click="requestBalanceTopup"
                                   :inactive="!topupAmount" class="mt-6" :show-spinner="isLoadingTopup">Topup Balance</TDSButton>
                    </div>
                </template>
            </DefaultCard>

            <DefaultCard title="Deduct eSIM Balance"
                         teaser="Deduct the eSIM balance of a user."
                         :error="deductError"
                         class="mb-6">
                <template #default>
                    <div>
                        <div class="amount-input">
                            <InputField :value.sync="deductionAmount" label="Amount" />
                            <div class="amount-units">
                                <TDSCheckbox :activated="deductionUnit === 'KB'" @update:activated="deductionUnit = 'KB'">KB</TDSCheckbox>
                                <TDSCheckbox :activated="deductionUnit === 'MB'" @update:activated="deductionUnit = 'MB'">MB</TDSCheckbox>
                                <TDSCheckbox :activated="deductionUnit === 'GB'" @update:activated="deductionUnit = 'GB'">GB</TDSCheckbox>
                            </div>
                        </div>

                         <InputField :value.sync="deductionReason" label="Reason" max-width="50%" />
                        <TDSButton button-style="primary-red" @click="requestBalanceDeduct" class="mt-6"
                                   :inactive="!deductionAmount || !deductionReason" :show-spinner="isLoadingDeduct">Deduct Balance</TDSButton>
                    </div>
                </template>
            </DefaultCard>
        </div>
        <TDSModal v-if="showModal && showModal === 'addProfile'"
                  @close="closeModal('addProfile')">
            <template #header>{{ $t("dataPlans.addProfile") }}</template>
            <template #body>
                <TDSError v-if="modalError" :message.sync="modalError"></TDSError>
                <p data-v-03b0a938="" class="card-teaser mb-4">Requesting an eSIM profile for user needs 2FA verification.</p>
                <TwoFAField :is-not-empty.sync="has2FA" />
            </template>
            <template #footer>
                <TDSButton button-style="primary-red" :inactive="isLoadingNewProfile || !has2FA"
                           :show-spinner="isLoadingNewProfile"
                           @click="addProfile()">{{ $t("dataPlans.addProfile") }}</TDSButton>
            </template>
        </TDSModal>
        <TDSModal v-else-if="showModal && showModal === 'profile'"
                  @close="closeModal('profile')">
            <template #header>{{ $t("dataPlans.newProfile") }}</template>
            <template #body>
                <div>
                    <TDSError v-if="modalError" :message.sync="modalError"></TDSError>
                    <div class="profile-info pt-3" >
                        <div v-for="(item, label, index) in selectedProfile" class="profile-item" :key="index">
                            <label>{{ label | camelCaseToSeparated }}</label>
                            <div>
                                <a v-if="label.toLowerCase() === 'activationcode'" :href="getQRCode(item)"
                                   :download="'activationQRCode_' + selectedProfile.id">Download QR</a>
                                <span v-else-if="label.toLowerCase() === 'createdat' || label.toLowerCase() === 'lastmodifiedat' || label.toLowerCase() === 'lastseen'">
                                    {{ item | dateFormat }}
                                </span>
                                <span v-else>{{ item }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <InputField :value.sync="userCountry" label="Countrycode" />
                <TwoFAField :is-not-empty.sync="has2FA" />
                <TDSButton :inactive="isLoadingNewProfile || !has2FA"
                           button-style="primary-red" :show-spinner="isLoadingNewProfile"
                           @click="replaceProfile(selectedProfile.iccid)">{{ $t("dataPlans.replaceProfile") }}</TDSButton>
            </template>
        </TDSModal>
        <TDSModal v-else-if="showModal && showModal === 'deductData'"
                  @close="closeModal">
            <template #header> Deduct Data Package </template>
            <template #body>
                <table-list :columns="deductPlanColumns"
                            :rows="selectedPackage">
                    <template slot="column_balance" slot-scope="{data}">
                        {{ data.balanceInByte | byteFormat }}
                    </template>
                    <template slot="column_country" slot-scope="{data}">
                        {{ data.countrySetSymbol }}
                    </template>
                    <template slot="column_expiry" slot-scope="{data}">
                        {{ data.expiresAt | dateFormat }}
                    </template>

                </table-list>
                <div>
                    <div class="amount-input">
                        <InputField :value.sync="deductionAmount" label="Amount" />
                        <div class="amount-units">
                            <TDSCheckbox :activated="deductionUnit === 'KB'" @update:activated="deductionUnit = 'KB'">KB</TDSCheckbox>
                            <TDSCheckbox :activated="deductionUnit === 'MB'" @update:activated="deductionUnit = 'MB'">MB</TDSCheckbox>
                            <TDSCheckbox :activated="deductionUnit === 'GB'" @update:activated="deductionUnit = 'GB'">GB</TDSCheckbox>
                        </div>
                    </div>

                    <InputField :value.sync="deductionReason" label="Reason" max-width="50%" />
                    <TDSButton button-style="primary-red" @click="deductSpecificDeduct" class="mt-6"
                               :inactive="!deductionAmount || !deductionReason" :show-spinner="isLoadingDeduct">Deduct Balance</TDSButton>
                </div>
            </template>

        </TDSModal>
    </div>
</template>

<script lang="ts">
import {Component, Vue} from "vue-property-decorator";
import DefaultCard from "@/components/partials/DefaultCard.vue";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import TDSError from "@/components/common/TDSError.vue";
import TDSButton from "@/components/common/TDSButton.vue";
import TableList, {TableListColumnConfig} from "@/components/common/TableList.vue";
import InputField from "@/components/common/InputField.vue";
import SelectField from "@/components/common/SelectField.vue";
import TDSModal from "@/components/common/TDSModal/TDSModal.vue";
import {DentUser} from "@/interfaces/entities/DentUser";
import TDSCheckbox from "@/components/common/TDSCheckbox.vue";
import QRCode from "qrcode";
import TwoFAField from "@/components/common/TwoFAField.vue";

@Component({
    components: {
        TwoFAField,
        TDSCheckbox,
        DefaultCard,
        TDSSpinner,
        TDSError,
        TDSButton,
        TableList,
        InputField,
        SelectField,
        TDSModal
    }
})
export default class UserDataPlans extends Vue {
    private profilesError: string = "";
    private plansError: string = "";
    private topUpError: string = "";
    private deductError: string = "";
    private modalError: string = "";
    private isLoadingProfile: boolean = false;
    private isLoadingNewProfile: boolean = false;
    private isLoadingPlans: boolean = false;
    private isLoadingTopup: boolean = false;
    private isLoadingDeduct: boolean = false;
    private user: any = {};
    private profiles: any = [];
    private dataPlans: any = [];
    private selectedProfile: any = {};
    private showModal: string = "";
    private has2FA: boolean = false;
    private userCountry: string = "";
    // twoFactorCodeRegex: regexes.twoFACodeRegex,
    private profileToReplace: any = undefined;
    private topupAmount: number = 10;
    private topupUnit: string = "GB";
    // positiveNumberRegex: regexes.positiveNumberRegex,
    private isQaTopup: boolean = true;
    private deductionAmount: number = 1;
    private deductionUnit: string = "GB";
    private deductionReason: string = "";
    private selectedPackage: any;
    private isLoadingSpecificDeduct: boolean = false;

    created() {
        this.getUser();
        this.getProfile().then(this.getPlans);
    }

    get isMobile(): boolean {
        return this.$store.state.isMobile;
    }

    get profilesTableColumns(): TableListColumnConfig {
        return {
            // date: {label: "Created at"},
            status: {label: "Status"},
            lastActive: {label: "Last active at"},
            lastCountry: {label: "Last country"},
            iccid: {label: "ICCID"},
            imsi: {label: "IMSI"}
        };
    }
    get dataPlansTableColumns(): TableListColumnConfig {
        return {
            bonus: {label:"", width:"40px"},
            status: {label: this.$t("dataPlans.plans.status") as string},
            country: {label: "Restricted Features"},
            balance: {label: "Balance" },
            expiry: {label: "Expires at"},
            deduct: {label: "Deduct"}
        };
    }
    get deductPlanColumns(): TableListColumnConfig {
        return{
            country: {label: "Restricted Features"},
            balance: {label: "Balance"},
            expiry: {label: "Expires at"}
        };
    }

    get iccidOfEsimProfileToReplace(): string | undefined {
        if (!this.profileToReplace) return undefined;
        return this.profileToReplace.find((item: any): boolean => item.label.toLowerCase() === "iccid").value;
    }

    async getUser() {
        try {
            const response: DentUser = await this.$store.dispatch("FETCH_USER", this.$route.params.userId);
            this.user = response;
        } catch (e) {
            this.profilesError = this.$t("general.error.loading", [e.message]) + "";
        }
    }

    async getProfile() {
        this.isLoadingProfile = true;
        try {
            // const url: string = "/user/" + decodeURI(this.$route.params.msisdn) + "/esim/profiles";
            // const response: GetEsimResponseModel = await this.$http.get(url);
            const response: any = await this.$store.dispatch("FETCH_USER_PROFILES", this.$route.params.userId);
            this.profiles = response.esimProfiles;
            //     .map((profile: Array<UILabelItem>): Array<UILabelItem> => {
            //     return profile.sort((entryA: UILabelItem, entryB: UILabelItem): number => {
            //         if (entryA.sortIndex < entryB.sortIndex) return -1;
            //         if (entryA.sortIndex > entryB.sortIndex) return 1;
            //         return 0;
            //     });
            // }).sort((profileA: Array<UILabelItem>, profileB: Array<UILabelItem>): number => {
            //     return Date.parse(profileB.find((item: UILabelItem): boolean => item.label === "Last Active At")?.value)
            //         - Date.parse(profileA.find((item: UILabelItem): boolean => item.label === "Last Active At")?.value);
            // });
        } catch (e) {
            this.profilesError = `Error while fetching the profile informations: <br><pre>${e.response?.data?.detail ?? e.message}</pre>`;
            console.error("[DataPlans] Error while fetching the profile information.", e);
        } finally {
            this.isLoadingProfile = false;
        }
    }

    async getPlans() {
        this.isLoadingPlans = true;
        try {
            // const response: GetDataPlansResponseModel = await this.$http.get("/user/" + decodeURI(this.$route.params.msisdn) + "/esim/data");
            const response: any = await this.$store.dispatch("FETCH_USER_PLANS", this.$route.params.userId);
            // Sort TopUps
            // response.data.forEach((entry: DataPlansModel) => {
            //     if (entry.topUps) {
            //         entry.topUps.sort((entryA: TopUpModel, entryB: TopUpModel): number => {
            //             if (entryA.createdAt < entryB.createdAt) return -1;
            //             if (entryA.createdAt > entryB.createdAt) return 1;
            //             return 0;
            //         });
            //     }
            // });
            this.$set(this, "dataPlans", response);
        } catch (e) {
            if (e?.response?.status !== 404) {
                this.plansError = `Error while fetching data plans: <br><pre>${e.response?.data?.detail ?? e.message}</pre>`;
            }
            console.error("[DataPlans] Error while fetching the data plans information.", e);
        }
        this.isLoadingPlans = false;
    }

    async replaceProfile(iccid: string) {
        if (this.isLoadingNewProfile) return;
        this.isLoadingNewProfile = true;
        try {
            // const msisdn: string = encodeURI(this.$route.params.msisdn);
            // if (this.profileToReplace) {
            //     const url: string = `/user/${msisdn}/esim/${iccid}/replace?two_factor_auth_code=${this.twoFactorCode}`;
            //     await this.$http.put(url);
            // } else {
            //     const url: string = `/user/${msisdn}/esim/profiles`;
            //     await this.$http.post(url, {
            //         twoFactorCode: this.twoFactorCode
            //     });
            // }
            const response: any = await this.$store.dispatch("REPLACE_PROFILE", {
                userId: this.$route.params.userId,
                iccid: iccid,
                userCountry: this.userCountry
            });
            console.log(response);
            await this.$toastr.success("Profile successfully replaced.");
            this.showModal = "";
            this.profiles = [];
            await this.getProfile();
        } catch (e) {
            this.modalError = `Error while replacing profile.<br><pre>${e.response?.data?.detail || e.message}</pre>`;
        } finally {
            this.isLoadingNewProfile = false;
        }
    }

    async addProfile() {
        if (this.isLoadingNewProfile) return;
        this.isLoadingNewProfile = true;
        try {
            const response: any = await this.$store.dispatch("ADD_PROFILE", this.$route.params.userId);
            console.log(response);
            await this.$toastr.success("New profile successfully issued.");
            this.showModal = "";
            this.profiles = [];
            await this.getProfile();
            this.has2FA = false;
        } catch (e) {
            this.modalError = `Error while issuing new profile.<br><pre>${e.response?.data?.detail || e.message || "Error while adding the new profile."}</pre>`;
        } finally {
            this.isLoadingNewProfile = false;
        }
    }

    async requestBalanceTopup() {
        if (this.isLoadingDeduct) return;
        this.isLoadingTopup = true;
        try {
            await this.$store.dispatch("TOPUP_USER", {
                userId: this.$route.params.userId,
                type: this.isQaTopup ? "QATOPUP" : "MARKETING",
                size: this.topupAmount,
                unit: this.topupUnit
            });
            await this.$toastr.success("Topup successful.");

            await this.getPlans();
        } catch (e) {
            if (e?.response?.status !== 404) {
                this.topUpError = `Error while topup: <br><pre>${e.response?.data?.detail ?? e.message}</pre>`;
            }
            console.error("[DataPlans] Error while topup balance.", e);
        } finally {
            this.isLoadingTopup = false;
        }
    }
    async deductSpecificDeduct() {
        if(this.isLoadingSpecificDeduct) return;
        if(!this.selectedPackage || this.selectedPackage.length === 0) return;
        this.isLoadingSpecificDeduct = true;
        try {
            await this.$store.dispatch("DEDUCT_SPECIFIC_PACKAGE", {
                userId: this.$route.params.userId,
                size: this.deductionAmount,
                unit: this.deductionUnit,
                reason: this.deductionReason,
                balanceId: this.selectedPackage[0].balanceId
            });
            await this.$toastr.success("Deduct successful.");

            await this.getPlans();
        } catch (e) {
            if (e?.response?.status !== 404) {
                this.deductError = `Error while deducting: <br><pre>${e.response?.data?.detail ?? e.message} - ${e.response?.data.message || "Failed to deduct"}</pre>`;
            }
            console.error("[DataPlans] Error while deducting balance.", e);
            await this.$toastr.error("Error while deducting the data package.", 5000, true);
        } finally {
            this.deductionAmount = 0;
            this.deductionUnit = "GB";
            this.deductionReason = "";
            this.isLoadingSpecificDeduct = false;
            this.showModal = "";
}

    }

    async requestBalanceDeduct() {
        if (this.isLoadingDeduct) return;
        this.isLoadingDeduct = true;
        try {
            await this.$store.dispatch("DEDUCT_USER", {
                userId: this.$route.params.userId,
                size: this.deductionAmount,
                unit: this.deductionUnit,
                reason: this.deductionReason
            });
            await this.$toastr.success("Deduct successful.");

            await this.getPlans();
        } catch (e) {
            if (e?.response?.status !== 404) {
                this.deductError = `Error while deducting: <br><pre>${e.response?.data?.detail ?? e.message}</pre>`;
            }
            console.error("[DataPlans] Error while deducting balance.", e);
        } finally {
            this.isLoadingDeduct = false;
        }
    }


    private getQRCode(text: string | null | undefined): string {
        if (!text) return "";
        let QRUrl: string = "";
        QRCode.toDataURL(text, (err: any, url: any): any => {
            QRUrl = url;
        });
        return QRUrl;
    }

    private showProfile(profileId: number) {
        const profile = this.profiles.find((p: any) => p.id === profileId);
        this.selectedProfile = Object.fromEntries(
            Object.entries(profile).sort((a: any, b: any) => {
                // console.log(typeof a[1] + " | " + a[1]);
                a = typeof a[1] !== "object" ? a[1].toString() : "";
                b = typeof b[1] !== "object" ? b[1].toString() : "";
                if (a < b) {
                    return -1;
                }
                if (b > a) {
                    return 1;
                }

                return 0;
            }).filter((entry: any) => entry[1])
        );
        this.showModal = "profile";
    }

    private closeModal(type: string) {
        if(type === "profile") this.selectedProfile = {};
        this.showModal = "";
    }
    private openDeductModal(data: any){
        this.selectedPackage = [data];
        this.showModal = "deductData";
        this.deductError = "";
    }
}

</script>

<style lang="scss" scoped>
.deduct-select{
    display: flex;
    align-items: center;
    *{
        margin-right: 12px;
    }
    .tds-checkbox{
        margin-top: 6px;
    }
}
.deductLink {
    color: $fullRed;
    text-decoration: underline;
    text-transform: none;
}
.isBonus {
    background: transparent url("../../assets/icons/earn-coin-grey56.svg") no-repeat left center; // TODO: Add correct Icon
    height: 18px;
    width: 18px;
    background-size: 18px;
    display: block;
    @media only screen and (max-width: $breakPoint){
        height: 30px;
        width: 30px;
        background-size: 30px;
    }
}
.error {
    //@include errorMessage;
}

.dataplan-infos:first-child {
    //margin-top: $userDetailMargin;
}
.date {
    font-variant-numeric: tabular-nums;
}
.green {
    color: $tendencyGreen;
}

.amount-input {
    display: flex;
    align-items: center;

    .amount-units {
        display: flex;
        gap: 12px;
        margin-left: 12px;
        margin-top: 8px;
    }
}

::v-deep pre {
    white-space: pre-wrap;
}

.profile-info {
    display: flex;
    flex-wrap: wrap;

    .profile-item {
        display: flex;
        flex-direction: column;
        margin: 0 0 16px 0;
        min-width: 50%;

        label {
            @include fontCaption();
            display: block;
            margin-bottom: 4px;
        }

        span {
            color: $fullGrey;
        }

        span, a {
            text-transform: capitalize;
            font-size: 12px;
            line-height: 1;

            &.icon {
                height: 12px;
                width: 12px;
                background-size: 12px;
                background-color: transparent;
                background-repeat: no-repeat;
                background-position: center;
                margin-right: 4px;
                display: inline-table;
            }
        }

        a {
            @include linkRed();
        }

        > div {
            display: flex;
        }
    }

}
.card-item {
    display: flex;
    margin-bottom: 5px;
    justify-content: start;
    flex-grow: 1;
    margin-top: 8px;
    padding-bottom: 8px;
     > div{
         padding-right: 5px;
     }
    .content{
        flex-grow: 1;
        >div{
            display: flex;
            >div{
                flex-grow: 2;
            }
        }
    }
    &:not(last-child) {
        border-bottom: 1px solid $greyShade3
    }
}
</style>
